import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import type { CurrentUser } from './types';

/**
 * Selector shape — `state.Login.user` carries the backend-validated user.
 * Note the capital `L` to match the existing Redux store key.
 */
interface AuthSelectableState {
  Login?: {
    user?: Partial<CurrentUser> | Record<string, unknown> | null;
  };
}

/**
 * useCurrentUser — the canonical way to read the logged-in user in the modern stack.
 *
 * - Reads from the existing `state.Login.user` Redux slice (populated by the
 *   legacy verifyOtp thunk). No new state machinery introduced.
 * - Returns `null` when no token is present (i.e. the user is unauthenticated).
 * - Memoises so consumers that destructure can compare stable references.
 *
 * @example
 *   const user = useCurrentUser();
 *   if (!user) return <Navigate to="/login" replace />;
 */
export function useCurrentUser(): CurrentUser | null {
  const raw = useSelector((state: AuthSelectableState) => state.Login?.user);

  return useMemo<CurrentUser | null>(() => {
    if (!raw || typeof raw !== 'object') return null;
    const u = raw as Partial<CurrentUser>;
    if (!u.token) return null;
    return {
      token: u.token,
      userId: u.userId ?? '',
      profileId: u.profileId ?? '',
      profileName: u.profileName ?? '',
      roleId: u.roleId ?? '',
      roleName: u.roleName ?? '',
      userGroup: u.userGroup ?? '',
      userName: u.userName ?? '',
      firstName: u.firstName ?? '',
      lastName: u.lastName ?? '',
      tenantId: u.tenantId ?? '',
      clientId: u.clientId ?? '',
      avatarUrl: u.avatarUrl,
      expiresAt: u.expiresAt,
      forcePasswordChange: u.forcePasswordChange,
      isNewUser: u.isNewUser,
      refreshToken: u.refreshToken,
      themeColour: u.themeColour,
    };
  }, [raw]);
}

/**
 * `true` when there's a logged-in user with a valid token. Pair with
 * `useCurrentUser` when you only need a yes/no answer.
 */
export function useIsAuthenticated(): boolean {
  return useCurrentUser() !== null;
}
